import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import _startCase from 'lodash/startCase';
import _toLower from 'lodash/toLower';

import FormStepper from '../../components/FormStepper';
import ControlledForm from '../../components/ControlledForm';

import { useAuthorizedFormData } from '../../core/context/AuthorizedFormDataContext';
import { useDispatchEventReportData } from '../../core/context/DispatchEventReportDataContext';

import { DispatchEventReport } from '../../core/api/clients';

import { getColor } from '../../core/colors';

import useNavigateWithConfirm from '../../core/hooks/useNavigateWithConfirm';
import { useGlobalFormState } from '../../core/context/GlobalFormStateContext';

import DispatchEventFormValidationSchema from './DispatchEventFormValidationSchema';

import EventStep from './FormSteps/EventStep';
import WeatherStep from './FormSteps/WeatherStep';
import NarrativeStep from './FormSteps/NarrativeStep';
import FlightStep from './FormSteps/FlightStep';
import { FormValidationProvider } from '../../core/context/FormValidationContext';

function DispatchEventFormEdit(): JSX.Element {
  const authorizedFormData = useAuthorizedFormData();
  const { dispatchEventReport, update, updateStatus } = useDispatchEventReportData();
  const { setGlobalFormState } = useGlobalFormState();
  const [submitError, setSubmitError] = React.useState<boolean>(false);

  const navigate = useNavigate();
  const navigateWithConfirm = useNavigateWithConfirm();

  const {
    formState: {
      isDirty,
      dirtyFields,
      ...formStateRest
    },
    ...formMethods
  } = useForm<DispatchEventReport>({
    defaultValues: dispatchEventReport,
    mode: 'onBlur',
    resolver: yupResolver(DispatchEventFormValidationSchema),
  });

  React.useEffect(() => {
    formMethods.reset(dispatchEventReport);
  }, [dispatchEventReport]);

  React.useEffect(() => {
    const { lastModifiedBy } = formMethods.getValues();
    const isSystemUser = lastModifiedBy === 'System';

    setGlobalFormState(isDirty, isSystemUser);
  }, [isDirty, formMethods]);

  const handleSave = () => update(formMethods.getValues());

  const onSubmit = () => {
    setSubmitError(false);
    update(formMethods.getValues(), () => {
      navigate(`/form/dispatch-event/${dispatchEventReport.id}/review`);
    });
  };

  const onError = () => {
    setSubmitError(true);
  };

  const isRevenueType = () => {
    const formValues = formMethods.getValues();
    return formValues.typeOfFlight === 'Revenue (part 121)';
  };

  return (
    <ControlledForm
      onSubmit={formMethods.handleSubmit(onSubmit, onError)}
      formProps={{
        noValidate: true,
      }}
    >
      <FormStepper
        control={formMethods.control}
        color={getColor(authorizedFormData.metadata?.color ?? 'grey')}
        steps={['Flight & Crew', 'Event', 'Weather', 'Narrative']}
        schema={DispatchEventFormValidationSchema.describe()}
        title="Dispatch Event Report"
        // eslint-disable-next-line max-len
        reportedBy={`${dispatchEventReport.employeeName} [${dispatchEventReport.employeeId}], ${_startCase(_toLower(dispatchEventReport.employeeTitle ?? ''))}, ${dispatchEventReport.employeeLocation}`}
        enableAutoSave={dispatchEventReport.lastModifiedBy !== 'System'}
        onSave={handleSave}
        saveStatus={updateStatus}
        lastSaved={dispatchEventReport.lastModifiedBy === 'System' ? null : dispatchEventReport.lastModifiedOn}
        isDirty={isDirty}
        onClose={() => navigateWithConfirm(authorizedFormData.metadata?.route ?? '')}
        submitError={submitError}
      >
        {({ activeStep }) => (
          <FormProvider formState={{ isDirty, dirtyFields, ...formStateRest }} {...formMethods}>
            <FormValidationProvider<DispatchEventReport>>
              <FlightStep visible={activeStep === 0} isRevenue={isRevenueType()} />
              <EventStep visible={activeStep === 1} />
              <WeatherStep visible={activeStep === 2} />
              <NarrativeStep visible={activeStep === 3} />
            </FormValidationProvider>
          </FormProvider>
        )}
      </FormStepper>
    </ControlledForm>
  );
}

export default DispatchEventFormEdit;
